import { FC } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router-dom';
import OnFarmBillingAgreementModal from '../components/on-farm-billing-agreement/OnFarmBillingAgreementModal';
import { useOnFarmActionBlocked } from '../hooks/useOnFarmActionBlocked';
import { ON_FARM_PATHS } from '../constants/routePaths';
import OnFarmCanisterContents from '../pages/on-farm/inventory-management/OnFarmCanisterContents';
import OnFarmDeposit from '../pages/on-farm/inventory-management/OnFarmDeposit';
import OnFarmDiscard from '../pages/on-farm/inventory/OnFarmDiscard';
import OnFarmEditAnimal from '../pages/on-farm/animal-management/OnFarmEditAnimal';
import OnFarmInventory from '../pages/on-farm/inventory/OnFarmInventory';
import OnFarmInventoryItem from '../pages/on-farm/inventory/OnFarmInventoryItem';
import OnFarmSellTransfer from '../pages/on-farm/inventory/OnFarmSellTransfer';
import OnFarmUse from '../pages/on-farm/inventory/OnFarmUse';
import OnFarmWithdraw from '../pages/on-farm/inventory/OnFarmWithdraw';
import OnFarmLocationManagement from '../pages/on-farm/location-management/OnFarmLocationManagement';
import OnFarmMove from '../pages/on-farm/inventory-management/OnFarmMove';
import OnFarmSettings from '../pages/on-farm/settings/OnFarmSettings';
import OnFarmOutstandingBalanceBanner from '../pages/on-farm/OnFarmOutstandingBalanceBanner';
import OnFarmBillingAgreementBanner from '../pages/on-farm/OnFarmBillingAgreementBanner';
import { OnFarmAgreementGateProvider, useOnFarmAgreementGate } from '../pages/on-farm/OnFarmAgreementGateContext';
import NotAuthorized from '../pages/error-pages/NotAuthorized';
import { PermissionService } from '../services/permission.service';

type OnFarmRoutesProps = {
  permissionService: PermissionService;
};

const OnFarmLayout = ({ canAcceptAgreement }: { canAcceptAgreement: boolean }) => {
  const { status, showAgreementModal, agreementSubmitting, closeAgreementModal, acceptAgreement } = useOnFarmAgreementGate();

  return (
    <div className="on-farm-layout">
      <OnFarmOutstandingBalanceBanner />
      <OnFarmBillingAgreementBanner canAccept={canAcceptAgreement} />
      <Outlet />
      {status && canAcceptAgreement && (
        <OnFarmBillingAgreementModal
          isOpen={showAgreementModal}
          status={status}
          submitting={agreementSubmitting}
          onClose={closeAgreementModal}
          onAccept={async () => {
            await acceptAgreement();
          }}
        />
      )}
    </div>
  );
};

const OnFarmRoutes: FC<OnFarmRoutesProps> = ({ permissionService }) => {
  const canAcceptAgreement = permissionService.userHasTeamManagementPermission() || permissionService.userHasRSGAdminPermission();

  return (
    <OnFarmAgreementGateProvider>
      <OnFarmRoutesInner permissionService={permissionService} canAcceptAgreement={canAcceptAgreement} />
    </OnFarmAgreementGateProvider>
  );
};

type OnFarmRoutesInnerProps = OnFarmRoutesProps & {
  canAcceptAgreement: boolean;
};

const OnFarmRoutesInner: FC<OnFarmRoutesInnerProps> = ({ permissionService, canAcceptAgreement }) => {
  const isOnFarmLocked = useOnFarmActionBlocked();
  const canTransact = permissionService.userHasInventoryPermission() && !isOnFarmLocked;

  const guarded = (element: JSX.Element) => (canTransact ? element : <NotAuthorized />);

  return (
    <Routes>
      <Route element={<OnFarmLayout canAcceptAgreement={canAcceptAgreement} />}>
        <Route index element={<Navigate to={ON_FARM_PATHS.INVENTORY} replace />} />
        <Route path={ON_FARM_PATHS.INVENTORY} element={<OnFarmInventory />} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.EDIT} element={guarded(<OnFarmEditAnimal />)} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.SELL_TRANSFER} element={guarded(<OnFarmSellTransfer />)} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.FARM_USE} element={guarded(<OnFarmUse />)} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.WITHDRAW} element={guarded(<OnFarmWithdraw />)} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.DISCARD} element={guarded(<OnFarmDiscard />)} />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM} element={<OnFarmInventoryItem />} />
        <Route
          path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM_ + ON_FARM_PATHS.EDIT}
          element={guarded(<OnFarmEditAnimal />)}
        />
        <Route
          path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM_ + ON_FARM_PATHS.SELL_TRANSFER}
          element={guarded(<OnFarmSellTransfer />)}
        />
        <Route path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM_ + ON_FARM_PATHS.FARM_USE} element={guarded(<OnFarmUse />)} />
        <Route
          path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM_ + ON_FARM_PATHS.WITHDRAW}
          element={guarded(<OnFarmWithdraw />)}
        />
        <Route
          path={ON_FARM_PATHS.INVENTORY_ + ON_FARM_PATHS.ITEM_ + ON_FARM_PATHS.DISCARD}
          element={guarded(<OnFarmDiscard />)}
        />
        <Route path={ON_FARM_PATHS.EDIT} element={guarded(<OnFarmEditAnimal />)} />
        <Route path={ON_FARM_PATHS.SELL_TRANSFER} element={guarded(<OnFarmSellTransfer />)} />
        <Route path={ON_FARM_PATHS.FARM_USE} element={guarded(<OnFarmUse />)} />
        <Route path={ON_FARM_PATHS.WITHDRAW} element={guarded(<OnFarmWithdraw />)} />
        <Route path={ON_FARM_PATHS.DISCARD} element={guarded(<OnFarmDiscard />)} />
        <Route path={ON_FARM_PATHS.DEPOSIT} element={guarded(<OnFarmDeposit />)} />
        <Route path={ON_FARM_PATHS.MOVE} element={guarded(<OnFarmMove />)} />
        <Route path={ON_FARM_PATHS.CANISTER_CONTENTS} element={<OnFarmCanisterContents />} />
        <Route path={ON_FARM_PATHS.LOCATION_MANAGEMENT} element={<OnFarmLocationManagement />} />
        <Route path={ON_FARM_PATHS.SETTINGS} element={<OnFarmSettings />} />
      </Route>
    </Routes>
  );
};

export default OnFarmRoutes;
